import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormGroup, LinearProgress, TextField, Box, Typography } from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon, Edit, Add, Delete } from '@mui/icons-material';

import Uploader from 'components/Uploader';
import AsyncImage from 'components/AsyncImage';

import { enqueueSnackbarError } from 'lib/helpers';
import { deleteHomeFeatured, updateAppHomeFeatured, updateFeaturedImage } from 'lib/models/appConfig';

import useSuccessSnackbar from 'hooks/useSuccessSnakbar';

import ConfirmationDialog from 'components/ConfirmationDialog';

type Props = {
	initialize: HomeFeatured;
	onClose: () => void;
};

function HomeFeaturedEditionForm({ initialize, onClose }: Props) {
	const { t } = useTranslation();

	const newFeatured = !initialize?._id;

	const successSnackbar = useSuccessSnackbar();

	const [isLoading, setLoading] = useState<boolean>(true);
	const [featured, setFeatured] = useState<HomeFeatured>(getDefaultFeatured());
	const [showUploader, setShowUploader] = useState(false);

	const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
	const [openDeleteConfirm, setDeleteConfirm] = useState<boolean>(false);

	const validScreens = ['Account', 'Catalog', 'EventsAndArtists', 'Orders', 'SearchScreen', 'Store', 'Wallet'];

	const isUrlValid = () => {
		if (!featured.url) return false;
		const hasHttp = featured.url.includes('http');
		if (hasHttp) return true;
		return validScreens.includes(featured.url);
	};

	const canSave = !!featured.title.es && !!featured.url && isUrlValid();

	const loading = isLoading;

	useEffect(() => {
		if (!newFeatured && initialize) {
			setFeatured(initialize);
		}
		setLoading(false);
	}, [initialize, newFeatured]);

	const onUpdate = (key: keyof HomeFeatured) => {
		return (featured: any) => {
			const internalizationKeys = ['title', 'linkText'];

			const value = internalizationKeys.includes(key) ? { es: featured.target.value } : featured.target.value;

			if (featured) {
				setFeatured((current) => ({ ...current, [key]: value }));
			}
		};
	};

	async function save() {
		try {
			setLoading(true);
			await updateAppHomeFeatured(featured);
			successSnackbar();
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setLoading(false);
		onClose();
	}

	async function deleteFeatured() {
		try {
			setLoading(true);
			await deleteHomeFeatured(featured);
			successSnackbar();
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setLoading(false);
		onClose();
	}

	async function uploadImage(file: File): Promise<boolean> {
		if (featured._id && showUploader) {
			const updatedFeatured = await updateFeaturedImage(featured._id, file);
			return !!updatedFeatured;
		}
		return false;
	}

	function onUploaderClose(_uploaded: boolean) {
		setShowUploader(false);
	}

	const confirmSave = (confirm: boolean) => {
		if (confirm) {
			save();
			setOpenModalConfirm(false);
		} else {
			setOpenModalConfirm(false);
		}
	};

	const confirmDelete = (confirm: boolean) => {
		if (confirm) {
			deleteFeatured();
			setOpenModalConfirm(false);
		} else {
			setOpenModalConfirm(false);
		}
	};

	return (
		<Box>
			{openModalConfirm && (
				<ConfirmationDialog
					title={'Confirmar edición'}
					description={'¿Estás seguro que querés guardar los datos?'}
					warningMessage={
						'Estás modificando contenido que afectará la visualización de la pantalla principal de la aplicación.'
					}
					onClose={confirmSave}
				></ConfirmationDialog>
			)}

			{openDeleteConfirm && (
				<ConfirmationDialog
					title={'Confirmar eliminación'}
					description={'¿Estás seguro que querés eliminar el destacado?'}
					warningMessage={
						'Estás modificando contenido que afectará la visualización de la pantalla principal de la aplicación.'
					}
					onClose={confirmDelete}
				></ConfirmationDialog>
			)}

			{showUploader && (
				<Uploader
					id="featured-image"
					name="featured-file"
					upload={uploadImage}
					onClose={onUploaderClose}
					open={true}
					warningMessage="La imagen debe ser de 343X105 px"
				/>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: 1, justifyContent: 'flex-end' }}>
				<Button variant="contained" color="secondary" size="large" startIcon={<CloseIcon />} onClick={onClose}>
					{t('common:close')}
				</Button>
			</Box>

			{loading && <LinearProgress sx={{ mb: 3 }} />}

			<FormGroup>
				<Box>
					<TextField
						id="title"
						label={t('common:title')}
						fullWidth={true}
						value={featured.title?.es ?? ''}
						variant="outlined"
						sx={{ mb: 3 }}
						onChange={onUpdate('title')}
						disabled={loading}
					/>

					<TextField
						id="link"
						label={'Texto del link'}
						fullWidth={true}
						value={featured.linkText.es}
						variant="outlined"
						sx={{ mb: 3 }}
						onChange={onUpdate('linkText')}
						disabled={loading}
						multiline
					/>

					<TextField
						id="url"
						label={t('common:url')}
						fullWidth={true}
						value={featured.url}
						variant="outlined"
						sx={{ mb: 3 }}
						onChange={onUpdate('url')}
						disabled={loading}
						multiline
						error={!!featured.url && !isUrlValid()}
						helperText={
							!!featured.url && !isUrlValid()
								? 'La URL debe contener "http" o ser una de las pantallas válidas'
								: ''
						}
					/>

					<Typography variant="caption">
						Pantallas disponibles: Account, Catalog, EventsAndArtists, Orders, SearchScreen, Store, Wallet
					</Typography>

					{!!featured._id && (
						<Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
							{/* Wide image */}
							<Box
								onClick={() => setShowUploader(true)}
								sx={{
									width: 350,
									mt: 2,
									mb: 1,
									borderRadius: 1,
									borderWidth: 1,
									borderStyle: 'solid',
									borderColor: 'gray',
									padding: 1,
									cursor: 'pointer',
									':hover': {
										backgroundColor: 'lightgray',
									},
									position: 'relative',
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'center',
									alignItems: 'centers',
								}}
							>
								{featured.image ? (
									<Edit sx={{ position: 'absolute', top: 2, right: 2 }} />
								) : (
									<Add sx={{ position: 'absolute', top: 2, right: 2 }} />
								)}

								{featured.image ? (
									<AsyncImage
										id={featured.image}
										style={{
											width: 328,
											height: 214,
											borderRadius: 5,
											borderWidth: 1,
											borderStyle: 'solid',
											borderColor: 'gray',
											marginTop: 24,
										}}
									/>
								) : (
									<Box
										sx={{
											width: 328,
											height: 214,
											borderRadius: 5,
											borderWidth: 1,
											borderStyle: 'solid',
											borderColor: 'gray',
											backgroundColor: 'lightgray',
											marginTop: 24,
										}}
									/>
								)}
							</Box>
						</Box>
					)}
				</Box>

				<Button
					variant="contained"
					color="primary"
					size="large"
					sx={{ m: 1 }}
					startIcon={<SaveIcon />}
					onClick={() => setOpenModalConfirm(true)}
					disabled={!canSave}
				>
					Guardar destacado
				</Button>

				{featured._id && (
					<Button
						variant="contained"
						color="error"
						size="large"
						sx={{ m: 1 }}
						startIcon={<Delete />}
						onClick={() => setDeleteConfirm(true)}
					>
						Eliminar
					</Button>
				)}
			</FormGroup>
		</Box>
	);
}

export default HomeFeaturedEditionForm;

function getDefaultFeatured(): HomeFeatured {
	return {
		title: { es: '' },
		linkText: { es: '' },
		url: '',
		image: '',
	};
}
